import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Popover, PopoverSurface, PopoverTrigger, makeStyles, tokens } from '@fluentui/react-components';
import { ChevronDown16Regular } from '@fluentui/react-icons';
import { SearchableMultiSelect, type SearchableMultiSelectOption } from './fleet/SearchableMultiSelect';

const useStyles = makeStyles({
  trigger: { width: '100%', minWidth: 0, justifyContent: 'space-between', fontWeight: tokens.fontWeightRegular },
  text: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'start' },
  surface: { width: '320px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
});

/**
 * A picker that opens from its cell (wave 4 §1b): the cell says what is chosen, and its button opens the fleet's searchable
 * multi-select (ptc-tax-lifecycle's, lifted) in a popover, with an optional action under the list ("New custom line…").
 * `single` keeps one choice: the newest pick replaces the last, and the popover closes.
 */
export default function UsesPicker({
  label,
  options,
  selected,
  onChange,
  placeholder,
  action,
  single = false,
}: {
  /** The trigger's accessible name ("Used as, account 4010"). */
  label: string;
  options: SearchableMultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  action?: { label: string; onClick: () => void };
  single?: boolean;
}) {
  const styles = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const text = selected.map((v) => options.find((o) => o.value === v)?.label ?? v).join(', ');

  const change = (next: string[]) => {
    if (!single) return onChange(next);
    const added = next.filter((v) => !selected.includes(v));
    onChange(added.length ? [added[added.length - 1]] : next);
    if (added.length) setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(_e, data) => setOpen(data.open)} inline positioning="below-start" trapFocus>
      <PopoverTrigger disableButtonEnhancement>
        <Button className={styles.trigger} size="small" aria-label={label} aria-expanded={open} icon={<ChevronDown16Regular />} iconPosition="after">
          <span className={styles.text}>{text || placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        <SearchableMultiSelect
          label={label}
          options={options}
          selected={selected}
          onChange={change}
          searchPlaceholder={t('searchableMultiSelect.placeholder')}
          hideListActions={single}
        />
        {action && (
          <Button
            size="small"
            appearance="subtle"
            onClick={() => {
              setOpen(false);
              action.onClick();
            }}
          >
            {action.label}
          </Button>
        )}
      </PopoverSurface>
    </Popover>
  );
}
